import React, { useEffect, useState, useRef } from 'react'
import { withRouter } from "react-router-dom"
import { connect } from "react-redux"
import { compose } from "redux"

import Notification from './components/Notification.jsx'

import {
  setUserLocation,
  addUserData,
  setOrderScreen,
  setFormattedAddress,
  setNotificationData,
  setUserData,
  setSubmitting
} from '../../redux/actions'

import translation from '../../translations/FirstOrderScreen'
import translationHeader from '../../translations/OrderBlockHeader'
import Api from '../../utils/api/Api'
import ReturnLabel from './components/ReturnLabel'
import locationIcon from '../../images/order/location.svg'
import heroImage from '../../images/figma-design/retail-image-5b5292.png'
import { ErrorOutline, LocationOn, Code, Business, Store } from '@material-ui/icons'

const FirstOrderScreen = props => {
  const { notification } = props;
  return (
    <main className="order-block-body order-block-body-grid">
      {notification.status && <Notification />}
      <CheckLocationInputs {...props} />
    </main>
  )
}

const ShowError = ({ error }) => {
  const lang = localStorage.getItem('lang')

  return (
    <div className="error-container" role="alert">
      <div className="error-content">
        <div className="error-icon">
          <ErrorOutline />
        </div>
        <div className="error-message">
          <h3 className="error-title">{translation.error[lang] || 'Error'}</h3>
          <p className="error-text">{error}</p>
        </div>
      </div>
    </div>
  )
}

const ShowReturnLabel = ({ returnLabel }) => {
  const lang = localStorage.getItem('lang')
  const serviceName = returnLabel?.servicePoint?.serviceCompany?.display || ''
  const hubName = returnLabel?.servicePoint?.postNLHub?.name || returnLabel?.servicePoint?.hub?.name || ''
  const blsCode = returnLabel?.servicePoint?.bls || ''
  const returnLabelCode = returnLabel?.returnLabel?.label || ''

  return (
    <div className="return-summary-card">
      <div className="return-summary-header">
        <h3 className="return-summary-title">{translation.orderDetails[lang]}</h3>
        <div className="status-badge">
          <span className="status-dot" />
          {translation.confirmed[lang]}
        </div>
      </div>
      <div className="return-summary-content">
        <div className="return-summary-body">
          <div className="summary-item">
            <LocationOn className="summary-icon" />
            <div>
              <span className="summary-label">{translation.blsCode[lang]}</span>
              <span className="summary-value">{blsCode}</span>
            </div>
          </div>
          <div className="summary-item">
            <Code className="summary-icon" />
            <div>
              <span className="summary-label">{translation.returnLabel[lang]}</span>
              <span className="summary-value">{returnLabelCode}</span>
            </div>
          </div>
          {!!serviceName && (
            <div className="summary-item">
              <Business className="summary-icon" />
              <div>
                <span className="summary-label">{translation.serviceCompany[lang]}</span>
                <span className="summary-value">{serviceName}</span>
              </div>
            </div>
          )}
          {!!hubName && (
            <div className="summary-item">
              <Store className="summary-icon" />
              <div>
                <span className="summary-label">{translation.station[lang]}</span>
                <span className="summary-value">{hubName}</span>
              </div>
            </div>
          )}
        </div>
        <div className="printPage return-summary-print">
          <ReturnLabel data={returnLabel} />
        </div>
      </div>
    </div>
  )
}

const CheckLocationInputs = ({ setSubmitting }) => {
  const lang = localStorage.getItem('lang')
  const [state, setState] = useState({
    showNumpad: true,
    code: null,
    error: null,
    variation: 1,
    loading: false,
    submitting: false,
  })

  const debounceTimerRef = useRef(null)
  const requestIdRef = useRef(0)

  const loadShipment = (value, requestId) => {
    updateState({loading: true})

    Api.placeReturnOrder(value).then(({ data }) => {
      // Only update state if this is the latest request
      if (requestId === requestIdRef.current) {
        updateState({
          returnLabel: data,
          showNumpad: false,
          error: null,
          code: value,
          loading: false,
        })
      }
    }).catch(async () => {
      // Only update state if this is the latest request
      if (requestId === requestIdRef.current) {
        updateState({
          returnLabel: null,
          showNumpad: false,
          error: translation.locationNotFound[lang],
          code: value,
          loading: false,
        })
      }
    })
  }

  const clear = (e) => {
    e.preventDefault()

    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    updateState({
      showNumpad: true,
      returnLabel: null,
      error: null,
      code: null,
      variation: state.variation + 1,
      loading: false,
      submitting: false,
    })
  }

  const updateState = (newState) => {
    const oldState = {...state}

    setState({...oldState, ...newState})
  }

  const onChangeCode = (e) => {
    const code = e.target.value
    updateState({ code })
  }

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Only trigger API call if code has at least 5 characters
    if (state.code && state.code.length >= 5) {
      // Increment request ID for the new request
      requestIdRef.current += 1
      const currentRequestId = requestIdRef.current

      // Debounce the API call by 500ms
      debounceTimerRef.current = setTimeout(() => {
        loadShipment(state.code, currentRequestId)
      }, 500)
    } else {
      // Reset state if code is too short
      updateState({
        returnLabel: null,
        error: null,
        loading: false,
      })
    }

    // Cleanup timer on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [state.code])

  return (
    <form key={state.variation} className="first-screen-inputs" onSubmit={e => {e.preventDefault();}}>
      <div className="card-header">
        <div className="welcome-text">
          <img src={locationIcon} alt="" className="location-icon" />
          <h1>{translationHeader.titleB2b[lang]}</h1>
        </div>
      </div>

      <div className="card-top-section">
        <div className="input-block">
          <h2>{state.returnLabel ? translation.orderPlaced[lang] : translation.fillLocationCode[lang]}</h2>

          <div className="input-wrapper">
            <div className="return-input">
              <input
                value={state.code || ''}
                placeholder={'BLS code'}
                onChange={onChangeCode}
                autoComplete="off"
              />
            </div>
            <div className="numpad-keys-caption">e.g. BC12M</div>
          </div>
        </div>

        <div className="image-block">
          <img src={heroImage} alt="Dobbi Service" />
        </div>
      </div>

      <div className="card-bottom-section" style={state.showNumpad ? {  paddingBottom: '200px' } : { paddingBottom: '50px' }}>
        <div className="main-block">
          {state.error && !state.loading && (
            <ShowError error={state.error} />
          )}

          {state.returnLabel && !state.loading && (
            <ShowReturnLabel returnLabel={state.returnLabel} />
          )}

          {!state.showNumpad && !state.loading && !state.submitting && (
            <div className="clear-link-wrapper">
              <a href="#" className="clear-link" onClick={clear}>
                {state.code ? translation.orEnterNewCode[lang] : translation.enterNewCode[lang]}
              </a>
            </div>
          )}
        </div>

        <div className="contact-block">
          <p
            className="contact-caption"
            dangerouslySetInnerHTML={{__html: translation.bannerText(' of bel ons op <a href="tel:+31850187540">+31 (0)85 018 7540</a>')[lang]}}
          />
        </div>
      </div>
    </form>
  )
}

const mapStateToProps = ({ webView, userLocation, userData, notification }) => ({
  wv: webView,
  userLocation,
  userData,
  notification
});

const mapDispatchToProps = dispatch => {
  return {
    setOrderScreen: data => dispatch(setOrderScreen(data)),
    setFormattedAddress: data => dispatch(setFormattedAddress(data)),
    setUserLocation: data => dispatch(setUserLocation(data)),
    addUserData: data => dispatch(addUserData(data)),
    setNotificationData: data => dispatch(setNotificationData(data)),
    setUserData: data => dispatch(setUserData(data)),
    setSubmitting: data => dispatch(setSubmitting(data)),
  };
};

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(FirstOrderScreen);
